/* Impact 7.1: mirror the current form; never issue a cart request or analytics event. */
(() => {
  if (customElements.get('invzi-sticky-atc')) return;
  class InvziStickyATC extends HTMLElement {
    connectedCallback() {
      this.form = document.getElementById(this.dataset.formId);
      if (!this.form) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.button = this.querySelector('.invzi-sticky-atc__button');
      this.status = this.querySelector('[role="status"]');
      this.bar = this.querySelector('[role="region"]');
      this.section = this.closest('.shopify-section');
      this.queue = () => {
        if (!this.frame) this.frame = requestAnimationFrame(() => { this.frame = 0; this.update(); });
      };
      this.button.addEventListener('click', event => {
        event.preventDefault();
        event.stopPropagation();
        this.submit();
      }, options);
      this.querySelectorAll('[data-sticky-option-position]').forEach(select => {
        select.addEventListener('change', event => {
          event.stopPropagation();
          const position = select.dataset.stickyOptionPosition;
          // These are display-only controls without a form/name. Change the real
          // Impact option input so its existing variant request remains authoritative.
          const input = [...this.form.elements].find(element =>
            element.matches('input[data-option-position]') &&
            element.dataset.optionPosition === position && element.value === select.value
          );
          if (!input || input.disabled) {
            this.message('Please select this option in the main product options.');
            this.chooseOptions();
            return;
          }
          if (input.checked) return;
          this.changing = true;
          this.message('');
          this.update();
          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }, options);
      });
      this.form.addEventListener('variant:change', () => { this.changing = false; this.queue(); }, options);
      this.form.addEventListener('cart:error', event => {
        this.pending = false;
        this.message(event.detail?.error || 'Unable to add this item. Please try again.');
        this.queue();
      }, options);
      this.form.addEventListener('variant:add', () => {
        this.pending = false;
        this.message('Added to cart.');
        this.queue();
      }, options);
      document.addEventListener('change', event => {
        if (event.target.form !== this.form) return;
        if (event.target.matches('[data-option-position]')) this.changing = true;
        this.queue();
      }, options);
      document.addEventListener('dialog:after-show', this.queue, options);
      document.addEventListener('dialog:after-hide', this.queue, options);
      window.addEventListener('scroll', this.queue, { ...options, passive: true });
      window.addEventListener('resize', this.queue, options);
      window.visualViewport?.addEventListener('resize', this.queue, options);
      this.observer = new MutationObserver(records => {
        // Ignore our own rendered state to avoid observer feedback loops.
        if (records.some(record => !this.contains(record.target))) this.queue();
      });
      this.observer.observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['class', 'style', 'open', 'hidden', 'disabled', 'aria-busy', 'aria-hidden'] });
      this.resize = new ResizeObserver(this.queue);
      this.resize.observe(this.bar);
      this.queue();
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      this.resize?.disconnect();
      cancelAnimationFrame(this.frame);
    }
    message(text) {
      this.status.textContent = String(text);
      this.status.hidden = !text;
    }
    mainButton() { return this.form.querySelector('button[type="submit"]'); }
    visible(element) {
      const rect = element.getBoundingClientRect();
      const css = getComputedStyle(element);
      return rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.top < innerHeight && rect.right > 0 && rect.left < innerWidth && css.visibility !== 'hidden' && css.display !== 'none' && css.opacity !== '0';
    }
    overlayOpen() {
      if (getComputedStyle(document.documentElement).overflowY === 'hidden' || getComputedStyle(document.body).overflowY === 'hidden') return true;
      return [...document.querySelectorAll('dialog[open], [role="dialog"], [aria-modal="true"], cart-drawer[open], cart-notification-drawer[open], x-modal[open], x-drawer[open], .shopify-section--popup [open]')].some(el => !this.contains(el) && this.visible(el));
    }
    update() {
      const main = this.mainButton();
      if (!main || !this.form.isConnected) { this.hidden = true; return; }
      const variant = this.form.elements.namedItem('id');
      const invalid = !variant || variant.disabled || !/^\d+$/.test(variant.value) || !this.dataset.variantId;
      const busy = this.pending || this.changing || main.getAttribute('aria-busy') === 'true';
      this.invalid = invalid;
      this.button.disabled = busy || (!invalid && main.disabled);
      this.querySelectorAll('[data-sticky-option-position]').forEach(select => { select.disabled = Boolean(busy); });
      this.button.setAttribute('aria-busy', String(Boolean(busy)));
      this.button.textContent = busy ? (this.changing ? 'Updating…' : 'Adding…') : invalid ? 'Choose options' : main.textContent.trim();
      const previewBar = document.getElementById('PBarNextFrameWrapper');
      const previewHeight = previewBar && this.visible(previewBar) ? previewBar.getBoundingClientRect().height : 0;
      const offset = `${Math.ceil(previewHeight)}px`;
      if (this.style.getPropertyValue('--invzi-sticky-bottom') !== offset) this.style.setProperty('--invzi-sticky-bottom', offset);
      const rect = main.getBoundingClientRect();
      // A button below the viewport has a positive bottom: never show at first entry.
      const passed = rect.height > 0 && rect.bottom < -2 && window.scrollY > 0;
      const enabled = matchMedia('(min-width: 700px)').matches ? this.dataset.desktop === 'true' : this.dataset.mobile === 'true';
      // Hide at the page end, like Impact's native quick-add, so footer content remains reachable.
      const footer = document.querySelector('.footer');
      const atFooter = footer && this.visible(footer);
      this.hidden = !passed || !enabled || atFooter || this.overlayOpen();
      const clearance = this.hidden ? '0px' : `${Math.ceil(this.bar.getBoundingClientRect().height + previewHeight + 12)}px`;
      if (document.documentElement.style.getPropertyValue('--invzi-sticky-clearance') !== clearance) document.documentElement.style.setProperty('--invzi-sticky-clearance', clearance);
    }
    chooseOptions() {
      const picker = this.section?.querySelector('variant-picker');
      const invalid = [...this.form.elements].find(el => el.willValidate && !el.validity.valid);
      const editableGroup = [...(picker?.querySelectorAll('fieldset') || [])].find(group => group.querySelectorAll('input[type="radio"]:not(:disabled)').length > 1);
      const optionScope = editableGroup || picker;
      const target = invalid || optionScope?.querySelector('input:checked:not(:disabled), select:not(:disabled), button:not(:disabled), input:not(:disabled)') || this.mainButton();
      (invalid || picker || this.form).scrollIntoView({ block: 'center', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
      target?.focus({ preventScroll: true });
      this.hidden = true;
    }
    submit() {
      this.update();
      if (this.button.disabled) return;
      if (this.invalid || !this.form.checkValidity()) { this.chooseOptions(); return; }
      const main = this.mainButton();
      if (!main || main.disabled || main.getAttribute('aria-busy') === 'true') return;
      this.message('');
      // Submit the real form once; the proxy click must not reach app click delegation.
      // FormData is built by Impact at submission time (quantity, properties, selling plan).
      this.pending = true;
      this.button.disabled = true;
      this.button.setAttribute('aria-busy', 'true');
      this.form.requestSubmit(main);
      this.queue();
    }
  }
  customElements.define('invzi-sticky-atc', InvziStickyATC);
})();
